<template>
    <MainLayout>
        <div class="active-route-page">
            <div class="container">
                <template v-if="activeRouteStore.routeId">
                    <div class="active-route-header shadow-sm">
                        <div class="route-name">
                            <img src="@/assets/images/from-icon.svg" class="point-icon" alt="" />
                            <span class="text-h5 text-bold">{{ firstRoutePoint }}</span>
                        </div>
                        <div class="points-count">
                            <span class="point-icon"></span>
                            <span class="text-h5"> {{ routePointsCount }} зупинок</span>
                        </div>
                        <div class="route-name">
                            <img src="@/assets/images/to-icon.svg" class="point-icon" alt="" />
                            <span class="text-h5 text-bold">{{ lastRoutePoint }}</span>
                        </div>
                        <div class="divider"></div>
                    </div>
                    <div class="active-route-map">
                        <MapComponent
                            v-if="activeRouteStore.routeData.points?.length"
                            :waypoints="activeRouteStore.routeData.points"
                        />
                    </div>
                    <div class="active-route-actions">
                        <div
                            v-if="activeRouteStore.routeData.points?.length"
                            class="open-in-maps-btn shadow-sm"
                            @click.stop="openInNavigator"
                        >
                            <img src="@/assets/images/map-icon.svg" />
                        </div>
                        <div class="finish-route-btn shadow-sm" @click.stop="onFinishRoute">
                            <img src="@/assets/images/stop-route-icon.svg" alt="" />
                        </div>
                    </div>
                    <div class="active-route-body shadow-sm">
                        <ActiveRoutePoint
                            v-for="(point, index) in activeRouteStore.routeData.points"
                            :key="point.id"
                            :point-data="point"
                            :point-index="index"
                            :last-completed="lastCompletedPoint === point.id"
                            :first-uncompleted="firstUncompletedPoint === point.id"
                        />
                    </div>
                </template>
                <template v-else>
                    <div class="missing-active-route">
                        <span class="text-h4 text-grey text-center">
                            У вас немає активного маршруту
                        </span>
                    </div>
                </template>
            </div>

            <transition name="slide-in">
                <div
                    v-show="$route.query.overlay"
                    class="add-item-overlay"
                    @click.stop="hideOverlay"
                >
                    <div class="overlay-actions">
                        <div class="hide-overlay-btn shadow-sm" @click.stop="hideOverlay">
                            <img src="@/assets/images/close-overlay-icon.svg" alt="" />
                        </div>
                        <div class="add-parcel-btn" @click.stop="showAddParcelModal">
                            <span> Додати посилку </span>
                            <img src="@/assets/images/add-parcel-icon.svg" alt="" />
                        </div>
                        <div class="add-passenger-btn" @click.stop="showAddPassengerModal">
                            <span> Додати пасажира </span>
                            <img src="@/assets/images/add-passenger-icon.svg" alt="" />
                        </div>
                        <router-link
                            :to="{ name: 'createTransactionPage' }"
                            class="add-transaction-btn"
                        >
                            <span> Додати транзакцію </span>
                            <img src="@/assets/images/add-expense-icon.svg" alt="" />
                        </router-link>
                    </div>
                </div>
            </transition>
        </div>

        <EntityModal />
        <AddParcelModal @hide-overlay="hideOverlay" />
        <AddPassengerModal @hide-overlay="hideOverlay" />
        <ActiveRoutePointModal />
        <CommentModal />
        <FinishRouteSubmitModal
            ref="finishRouteSubmitModal"
            :form-submit-loader="showRouteFinishSubmitLoader"
            @submit-action="finishRoute"
        />
        <Transition name="fade">
            <LoaderComponent v-if="showLoader" />
        </Transition>
    </MainLayout>
</template>

<script>
import MainLayout from '@/layouts/MainLayout.vue'
import ActiveRoutePoint from '@/components/activeRoute/ActiveRoutePoint.vue'
import MapComponent from '@/components/MapComponent.vue'
import { useActiveRouteStore, useModalStore } from '@/stores/index.js'
import EntityModal from '@/components/modals/EntityModal.vue'
import fetchWrapper from '@/helpers/fetch-wrapper.js'
import AddParcelModal from '@/components/modals/AddParcelModal.vue'
import AddPassengerModal from '@/components/modals/AddPassengerModal.vue'
import LoaderComponent from '@/components/LoaderComponent.vue'
import { Geolocation } from '@capacitor/geolocation'
import FinishRouteSubmitModal from '@/components/modals/FinishRouteSubmitModal.vue'
import ActiveRoutePointModal from '@/components/modals/ActiveRoutePointModal.vue'
import CommentModal from '@/components/modals/CommentModal.vue'

export default {
    name: 'RouteView',
    components: {
        FinishRouteSubmitModal,
        AddPassengerModal,
        AddParcelModal,
        EntityModal,
        MapComponent,
        ActiveRoutePoint,
        ActiveRoutePointModal,
        CommentModal,
        MainLayout,
        LoaderComponent
    },
    setup() {
        const activeRouteStore = useActiveRouteStore()
        const modalStore = useModalStore()

        return { activeRouteStore, modalStore }
    },
    data() {
        return {
            routeData: {},
            showLoader: true,
            showRouteFinishSubmitLoader: false
        }
    },
    computed: {
        firstRoutePoint() {
            return this.activeRouteStore.routeData.points?.[0].address
        },

        lastRoutePoint() {
            return this.activeRouteStore.routeData.points?.[
                this.activeRouteStore.routeData.points?.length - 1
            ].address
        },

        routePointsCount() {
            return (
                this.activeRouteStore.routeData.points?.[
                    this.activeRouteStore.routeData.points?.length - 3
                ]?.sort || 0
            )
        },

        lastCompletedPoint() {
            for (let i = this.activeRouteStore.routeData.points?.length - 1; i >= 0; i--) {
                if (this.activeRouteStore.routeData.points[i].status.value === 'Completed') {
                    return this.activeRouteStore.routeData.points[i].id
                }
            }

            return null
        },

        firstUncompletedPoint() {
            return this.activeRouteStore.routeData.points?.find(
                (obj) => obj.status.value === 'Uncompleted'
            )?.id
        }
    },
    mounted() {
        this.activeRouteStore
            .getActiveRouteId()
            .then(() => {
                if (this.activeRouteStore.routeId) {
                    this.activeRouteStore.loadActiveRouteData()
                }
            })
            .finally(() => {
                this.showLoader = false

                if (this.$route.query.from === 'finishRoute') {
                    this.$nextTick(() => {
                        this.$refs.finishRouteSubmitModal.showModal()
                        this.$router.replace({ query: {} })
                    })
                }
            })
    },
    unmounted() {
        this.modalStore.activeRoutePointModal.show = false
        this.modalStore.activeRoutePointModal.pointId = null
        this.modalStore.activeRoutePointModal.firstUncompleted = false
        this.modalStore.activeRoutePointModal.lastCompleted = false
    },
    methods: {
        async openInNavigator() {
            this.showLoader = true

            if (
                !this.activeRouteStore.routeData.points ||
                this.activeRouteStore.routeData.points.length === 0
            ) {
                this.showLoader = false
                this.$toast.error('Немає точок для прокладання маршруту')

                return
            }

            try {
                const hasPermission = await Geolocation.checkPermissions()

                if (hasPermission.location === 'denied') {
                    this.showLoader = false
                    this.$toast.error('Надайте додатку доступ до точної геолокації')

                    return
                }
            } catch (e) {
                this.showLoader = false
                this.$toast.error('Увімкніть геолокацію')

                return
            }

            const location = await Geolocation.getCurrentPosition({ enableHighAccuracy: true })
            const currentLocation = [location?.coords?.latitude, location?.coords?.longitude]

            let coordinates = this.activeRouteStore.routeData.points
                .filter((waypoint) => waypoint.status.value === 'Uncompleted')
                .map((waypoint) => waypoint.coordinates.join(','))
                .slice(0, 9)

            if (currentLocation[0]?.length) {
                coordinates.unshift(currentLocation)
            } else {
                coordinates.unshift('My+location')
            }

            let url = 'https://www.google.com/maps/dir/' + coordinates.join('/')

            this.showLoader = false
            window.open(url, '_blank').focus()
        },

        hideOverlay() {
            const newQuery = { ...this.$route.query }

            delete newQuery.overlay
            this.$router.replace({ query: newQuery })
        },

        showAddParcelModal() {
            const points = this.activeRouteStore.routeData.points || []
            const hasUncompleted = points.some((p) => p.status?.value === 'Uncompleted')

            if (!hasUncompleted) {
                this.$toast.error('Неможливо створити посилку — всі точки маршруту виконані')
                return
            }

            this.modalStore.addParcelModal.show = true
        },

        showAddPassengerModal() {
            const points = this.activeRouteStore.routeData.points || []
            const hasUncompleted = points.some((p) => p.status?.value === 'Uncompleted')

            if (!hasUncompleted) {
                this.$toast.error('Неможливо створити пасажира — всі точки маршруту виконані')
                return
            }

            this.modalStore.addPassengerModal.show = true
        },

        onFinishRoute() {
            this.$refs.finishRouteSubmitModal.showModal()
        },

        finishRoute(odometerData) {
            this.showLoader = true
            this.showRouteFinishSubmitLoader = true

            fetchWrapper
                .post(
                    `/routes/update-status/${this.activeRouteStore.routeId}`,
                    {
                        status: 'Finished route',
                        ...odometerData
                    },
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                )
                .then(() => {
                    this.$refs.finishRouteSubmitModal.hideModal()
                    this.$toast.success('Маршрут завершено')
                    this.activeRouteStore.routeId = null
                    this.activeRouteStore.routeCompanyNumber = null
                    this.activeRouteStore.routeName = null
                    this.activeRouteStore.routeData = {}
                    this.$router.push({ name: 'homePage' })
                })
                .finally(() => {
                    this.showLoader = false
                    this.showRouteFinishSubmitLoader = false
                })
        }
    }
}
</script>

<style scoped lang="scss">
.main-layout {
    padding-bottom: 4.5rem;
}

.active-route-page {
    position: relative;
    padding: 1.25rem 0;
    height: 100%;

    .container {
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }
}

.active-route-header {
    position: relative;
    padding: 0.75rem 1.25rem;
    background: $white;
    border-radius: 0.8rem;
    overflow: hidden;
    z-index: 3;

    .route-name {
        display: flex;
        align-items: center;
        padding: 0.25rem 0;

        &:before {
            height: 1.5rem !important;
        }

        .point-icon {
            margin-right: 0.625rem;
            width: 1.25rem;
            min-width: 1.25rem;
            height: 1.25rem;
        }

        span {
            max-height: 1.875rem;
            overflow: hidden;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
        }
    }

    .points-count {
        display: flex;
        align-items: center;
        margin: 0.5rem 0;
        padding: 0.25rem 0;

        .point-icon {
            margin-right: 0.625rem;
            width: 0.625rem;
            min-width: 0.625rem;
            height: 0.625rem;
            border-radius: 50%;
            background: $grey;
            margin-left: 0.313rem;
        }
    }

    .route-name,
    .points-count {
        position: relative;
        z-index: 2;

        &:before {
            content: '';
            position: absolute;
            top: 50%;
            left: 0;
            width: 1.25rem;
            height: 1.25rem;
            background: $white;
            z-index: -1;
            transform: translateY(-50%);
        }
    }

    .divider {
        position: absolute;
        top: 50%;
        left: 1.8rem;
        border-left: 1px dashed $grey;
        height: calc(100% - 2.5rem);
        z-index: 1;
        transform: translateY(-50%);
    }
}

.active-route-map {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: $white;
    z-index: 1;
}

.active-route-actions {
    position: absolute;
    display: flex;
    flex-direction: column;
    top: 11rem;
    right: 1rem;
    z-index: 5;

    .open-in-maps-btn {
        display: flex;
        z-index: 1;
        border-radius: 50%;
        margin-bottom: 0.7rem;
        cursor: pointer;
    }

    .add-item-btn {
        display: flex;
        z-index: 1;
        border-radius: 50%;
        margin-bottom: 0.7rem;
        cursor: pointer;
    }

    .finish-route-btn {
        display: flex;
        z-index: 1;
        border-radius: 50%;
        cursor: pointer;
    }
}

.active-route-body {
    position: relative;
    padding: 0.5rem 1rem 0 1rem;
    background: $white;
    border-radius: 0.8rem;
    overflow: auto;
    max-height: 45vh;
    z-index: 3;

    .active-route-point {
        border-bottom: 1px solid $grey-light;

        &:last-of-type {
            border-bottom: none;
        }
    }
}

.missing-active-route {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
}

.add-item-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(2px);
    z-index: 505;

    .overlay-actions {
        position: absolute;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        top: 9rem;
        right: 1rem;
    }

    .hide-overlay-btn {
        display: flex;
        z-index: 1;
        border-radius: 50%;
        margin-bottom: 0.7rem;
        cursor: pointer;
    }

    .add-parcel-btn,
    .add-passenger-btn,
    .add-transaction-btn {
        display: flex;
        align-items: center;
        margin-bottom: 0.7rem;
        cursor: pointer;

        span {
            margin-right: 0.7rem;
        }
    }

    .add-transaction-btn {
        text-decoration: none;

        span {
            color: $black;
        }
    }
}

.slide-in-enter-active {
    animation: slide-in 0.3s;
}

.slide-in-leave-active {
    animation: slide-in 0.2s reverse;
}

@keyframes slide-in {
    0% {
        transform: translateX(100%);
    }
    100% {
        transform: translateX(0);
    }
}
</style>
